import { BikeRide } from "@/api/users/types";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

interface ExportData {
  overview: {
    totalRides: number;
    totalUsers: number;
    totalOffsetRides: number;
    totalDistance: number;
    totalOffsetDistance: number;
    totalCO2Offset: number;
  };
  bikes: Array<{
    model: string;
    serialNumber: string;
    status: string;
    batteryLevel: number;
    location: string;
    distanceTraveled: number;
  }>;
  users: Array<{
    name: string;
    totalRides: number;
    totalDistance: number;
    offsetDistance: number;
    nonOffsetDistance: number;
    co2Offset: number;
  }>;
  rides: BikeRide[];
}

export const exportStatisticsData = (data: ExportData, format: 'csv' | 'excel' | 'pdf') => {
  if (!data.rides.length && !data.bikes.length && !data.users.length) {
    toast.error("No data to export");
    return;
  }

  // Overview data
  const overviewData = [{
    'Total Rides': data.overview.totalRides,
    'Total Users': data.overview.totalUsers,
    'Total Offset Rides': data.overview.totalOffsetRides,
    'Total Distance (km)': data.overview.totalDistance.toFixed(2),
    'Total Offset Distance (km)': data.overview.totalOffsetDistance.toFixed(2),
    'Total CO2 Offset (kg)': data.overview.totalCO2Offset.toFixed(2)
  }];

  // Bikes data
  const bikesData = data.bikes.map(bike => ({
    'Model': bike.model,
    'Serial Number': bike.serialNumber,
    'Status': bike.status,
    'Battery Level': `${bike.batteryLevel}%`,
    'Location': bike.location,
    'Distance Traveled (km)': bike.distanceTraveled.toFixed(2)
  }));

  // Users data
  const usersData = data.users.map(user => ({
    'Name': user.name,
    'Total Rides': user.totalRides,
    'Total Distance (km)': user.totalDistance.toFixed(2),
    'Offset Distance (km)': user.offsetDistance.toFixed(2),
    'Non-Offset Distance (km)': user.nonOffsetDistance.toFixed(2),
    'CO2 Offset (kg)': user.co2Offset.toFixed(2)
  }));

  // Rides data
  const ridesData = data.rides.map(ride => ({
    'Date': new Date(ride.startTime).toLocaleDateString(),
    'Time': `${new Date(ride.startTime).toLocaleTimeString()} - ${new Date(ride.endTime).toLocaleTimeString()}`,
    'User': ride.userName,
    'Bike': ride.serialNumber || ride.bikeName || `Bike #${ride.bikeId}`,
    'Distance (km)': ride.distance.toFixed(2),
    'Duration (min)': ride.duration,
    'Route': `${ride.startLocation} → ${ride.endLocation}`,
            'CO2 Offset (kg)': ride.countTowardsOffset ? (ride.distance * 0.16272).toFixed(2) : '0.00'
  }));

  switch (format) {
    case 'csv':
      const csvContent = [
        '=== Overview ===',
        Object.keys(overviewData[0]).join(','),
        Object.values(overviewData[0]).join(','),
        '\n=== Bikes ===',
        Object.keys(bikesData[0]).join(','),
        ...bikesData.map(row => Object.values(row).join(',')),
        '\n=== Users ===',
        Object.keys(usersData[0]).join(','),
        ...usersData.map(row => Object.values(row).join(',')),
        '\n=== Rides ===',
        Object.keys(ridesData[0]).join(','),
        ...ridesData.map(row => Object.values(row).join(','))
      ].join('\n');
      
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvLink = document.createElement('a');
      csvLink.href = csvUrl;
      csvLink.setAttribute('download', `statistics_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
      break;

    case 'excel':
      const wb = XLSX.utils.book_new();
      
      // Add overview sheet
      const overviewWs = XLSX.utils.json_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(wb, overviewWs, "Overview");
      
      // Add bikes sheet
      const bikesWs = XLSX.utils.json_to_sheet(bikesData);
      XLSX.utils.book_append_sheet(wb, bikesWs, "Bikes");
      
      // Add users sheet
      const usersWs = XLSX.utils.json_to_sheet(usersData);
      XLSX.utils.book_append_sheet(wb, usersWs, "Users");
      
      // Add rides sheet
      const ridesWs = XLSX.utils.json_to_sheet(ridesData);
      XLSX.utils.book_append_sheet(wb, ridesWs, "Rides");
      
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const excelUrl = URL.createObjectURL(excelBlob);
      const excelLink = document.createElement('a');
      excelLink.href = excelUrl;
      excelLink.setAttribute('download', `statistics_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(excelLink);
      excelLink.click();
      document.body.removeChild(excelLink);
      break;

    case 'pdf':
      const doc = new jsPDF();
      
      // Overview section
      doc.text("Statistics Overview", 14, 14);
      autoTable(doc, {
        head: [Object.keys(overviewData[0])],
        body: overviewData.map(row => Object.values(row)),
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
      });
      
      // Bikes section
      doc.addPage();
      doc.text("Bikes", 14, 14);
      autoTable(doc, {
        head: [Object.keys(bikesData[0])],
        body: bikesData.map(row => Object.values(row)),
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
      });
      
      // Users section
      doc.addPage();
      doc.text("Users", 14, 14);
      autoTable(doc, {
        head: [Object.keys(usersData[0])],
        body: usersData.map(row => Object.values(row)),
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
      });
      
      // Rides section
      doc.addPage();
      doc.text("Rides", 14, 14);
      autoTable(doc, {
        head: [Object.keys(ridesData[0])],
        body: ridesData.map(row => Object.values(row)),
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
      });
      
      doc.save(`statistics_export_${new Date().toISOString().split('T')[0]}.pdf`);
      break;
  }

  toast.success(`Exporting statistics data as ${format.toUpperCase()}. File download will start shortly.`);
}; 